import React, { useEffect, useState } from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LightModeIcon from '@mui/icons-material/LightMode';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';

const drawerWidth = 280;
const navItems = [
    ['Expertise', 'expertise'],
    ['History', 'history'],
    ['Projects', 'projects'],
    ['Contact', 'contact']
];

function Navigation({parentToChild, modeChange}) {
    const {mode} = parentToChild;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen((prevState) => !prevState);
    };

    useEffect(() => {
        const handleScroll = () => {
            const navbar = document.getElementById("navigation");
            if (navbar) {
                const scrolled = window.scrollY > navbar.clientHeight;
                setScrolled(scrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const scrollToSection = (section) => {
        const expertiseElement = document.getElementById(section);
        if (expertiseElement) {
            expertiseElement.scrollIntoView({ behavior: 'smooth' });
        }
        setMobileOpen(false);
    };

    const drawer = (
        <Box className="navigation-bar-responsive" onClick={handleDrawerToggle} sx={{ textAlign: 'center', bgcolor: 'var(--bg-surface)', height: '100%' }}>
            <Box sx={{ py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '8px',
                    background: 'var(--gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Space Grotesk, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    color: '#ffffff'
                }}>
                    AH
                </Box>
            </Box>
            <Divider sx={{ borderColor: 'var(--border-color)' }} />
            <List sx={{ pt: 2 }}>
                {navItems.map((item) => (
                    <ListItem key={item[0]} disablePadding>
                        <ListItemButton 
                            sx={{ 
                                textAlign: 'center', 
                                py: 2,
                                color: 'var(--text-heading)',
                                '&:hover': {
                                    background: 'var(--bg-surface-hover)',
                                    color: 'var(--accent-primary)'
                                }
                            }} 
                            onClick={() => scrollToSection(item[1])}
                        >
                            <ListItemText 
                                primary={item[0]} 
                                primaryTypographyProps={{
                                    sx: { 
                                        fontFamily: 'Space Grotesk, sans-serif',
                                        fontSize: '0.95rem',
                                        letterSpacing: '1px'
                                    }
                                }} 
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider sx={{ borderColor: 'var(--border-color)', mt: 'auto' }} />
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <Button 
                    onClick={(e) => {
                        e.stopPropagation();
                        modeChange();
                    }}
                    startIcon={mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                    sx={{
                        fontFamily: 'Space Grotesk, sans-serif',
                        fontSize: '0.8rem',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        px: 2,
                        py: 1,
                        color: 'var(--text-body)',
                        '&:hover': {
                            borderColor: 'var(--accent-primary)',
                            color: 'var(--accent-primary)'
                        }
                    }}
                >
                    {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar 
                component="nav" 
                id="navigation" 
                className={`navbar-fixed-top${scrolled ? ' scrolled' : ''}`}
                elevation={0}
                sx={{ background: 'transparent' }}
            >
                <Toolbar className='navigation-bar' sx={{ minHeight: '64px !important' }}>
                    <IconButton
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' }, color: 'var(--text-heading)' }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': {
                            '& .logo-box': {
                                transform: 'rotate(5deg) scale(1.05)'
                            }
                        }
                    }}>
                        <Box 
                            className="logo-box"
                            sx={{ 
                                width: 40, 
                                height: 40, 
                                borderRadius: '8px',
                                background: 'var(--gradient-primary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontFamily: 'Space Grotesk, sans-serif',
                                fontWeight: 700,
                                fontSize: '1.2rem',
                                color: '#ffffff',
                                transition: 'var(--transition-bounce)'
                            }}
                        >
                            AH
                        </Box>
                    </Box>

                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, ml: 'auto', gap: 1 }}>
                        {navItems.map((item) => (
                            <Button 
                                key={item[0]} 
                                onClick={() => scrollToSection(item[1])} 
                                sx={{ 
                                    color: 'var(--text-heading)',
                                    fontFamily: 'Space Grotesk, sans-serif',
                                    fontSize: '0.85rem',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    mx: 0.5,
                                    px: 2,
                                    '&:hover': {
                                        background: 'var(--bg-surface-hover)',
                                        color: 'var(--accent-primary)'
                                    }
                                }}
                            >
                                {item[0]}
                            </Button>
                        ))}
                    </Box>

                    <Box sx={{ ml: 2, display: { xs: 'none', sm: 'block' } }}>
                        <IconButton
                            onClick={() => modeChange()}
                            sx={{
                                color: 'var(--text-heading)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                p: 1,
                                '&:hover': {
                                    borderColor: 'var(--accent-primary)',
                                    background: 'var(--bg-surface-hover)',
                                    color: 'var(--accent-primary)'
                                }
                            }}
                        >
                            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box component="nav">
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth,
                            bgcolor: 'var(--bg-main)',
                            borderRight: '1px solid var(--border-color)'
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>
        </Box>
    );
}

export default Navigation;
